<template>
  <div class="actions-div">
    <PrimeButton :disabled="isDisabled" @click="handleClick" variant="secondary" class="p-btn-root_secondary w-full">
      Email Gift Certificate
      <span v-if="isDisabled && countdown > 0" class="countdown-text"> ({{ countdown }}s) </span>
    </PrimeButton>
    <PrimeButton @click="handleCopyClick" variant="secondary" class="p-btn-root_secondary copy-btn">
      Copy Code to Clipboard
    </PrimeButton>
  </div>
</template>
<script setup lang="ts">
import { useSubscribeStore } from '@/stores/components/modals/subscription-modal';
import PrimeButton from 'primevue/button';
import { useBillingStore } from '~/stores/person/billing';
import { inject, type Ref } from 'vue';

const store = useSubscribeStore();

const props = defineProps({
  isEmailSent: {
    type: Boolean,
    required: true,
  },
});

const isLoading = ref(false);
const isDisabled = ref(false);
const countdown = ref(0);

const emit = defineEmits(['handleEmailClick', 'handleCopyCode']);

const giftCertActionTaken = inject<Ref<boolean>>('giftCertActionTaken');

const handleClick = async () => {
  isLoading.value  = true;
  isDisabled.value = true;
  if (giftCertActionTaken) giftCertActionTaken.value = true;
  try {
    const giftCertCode = store.giftCertCode;
    const giftCertEmail = store.giftCertEmail;
    const giftCertName = store.giftCertName;
    
    if (giftCertCode && giftCertEmail && giftCertName) {
      const billingStore = useBillingStore();
      await billingStore.sendGiftCertificateEmail(giftCertName, giftCertEmail, giftCertCode);

      await navigator.clipboard.writeText(giftCertCode);
      emit('handleEmailClick');
      //Start 10-second cooldown
      startCountdown(10);
    } else {
      console.error('Missing gift certificate details for email');
      isDisabled.value = false;
    }
  } catch (error) {
    console.error('Failed to send gift certificate email:', error);
    isDisabled.value = false;
  } finally {
        isLoading.value = false;
  }
};

const startCountdown = (seconds : number) => {
  countdown.value = seconds;
  const interval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(interval);
      isDisabled.value = false;
    }
  }, 1000);
};

const handleCopyClick = async () => {
  if (giftCertActionTaken) giftCertActionTaken.value = true;
  emit('handleCopyCode');
  try {
    const giftCertCode = store.giftCertCode;
    if (giftCertCode) {
      await navigator.clipboard.writeText(giftCertCode);
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
  }
};
</script>
<style lang="scss" scoped>
.actions-div {
    @apply flex justify-end items-center gap-3 self-stretch;
}

.copy-btn {
    @apply focus:border-white-700 focus:outline-none focus:ring-1 focus:ring-blue-700 w-full;
}
</style>